import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from './Button'

/**
 * Bắt lỗi render để **một màn hình hỏng không làm trắng cả trang quản trị**.
 *
 * Vì sao cần: 2026-09-11 trang `/seo` trả về màn hình trắng hoàn toàn — không chữ, không
 * thông báo, `#root` rỗng. Nguyên nhân chỉ là một bảng tra thiếu một khoá
 * (`EDIT_HREF['page']` là `undefined`, gọi nó ném TypeError). Một dòng dữ liệu lạ đã hạ
 * toàn bộ SPA, và thứ duy nhất người dùng thấy là "trang trắng" — không đủ thông tin để
 * báo lỗi, cũng không có đường quay lại.
 *
 * React **không** có cơ chế nào khác để bắt lỗi render: hook không làm được, chỉ class
 * component với `componentDidCatch`/`getDerivedStateFromError`. Đây là lý do duy nhất còn
 * một class component trong `apps/admin`.
 *
 * ⚠️ Nó **không** bắt lỗi trong event handler, `setTimeout`, hay promise bị reject — những
 * chỗ đó React không ném qua cây component. Đừng tưởng có cái này là mọi lỗi đều an toàn.
 */
interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Console là nơi duy nhất còn dấu vết khi người dùng gửi ảnh chụp màn hình.
    console.error('[admin] lỗi render:', error, info.componentStack)
  }

  override render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="mx-auto max-w-2xl rounded-card-sm border border-danger/30 bg-danger-tint p-6">
        <h1 className="font-heading text-lg text-ink">Màn hình này gặp lỗi</h1>
        <p className="mt-2 text-sm text-ink-muted">
          Phần còn lại của trang quản trị vẫn dùng được — chọn một mục khác ở menu bên trái.
          Nếu cần báo lỗi, gửi kèm dòng dưới đây.
        </p>
        <pre className="mt-3 overflow-x-auto rounded-button bg-white p-3 font-mono text-xs text-danger">
          {error.message}
        </pre>
        <div className="mt-4 flex gap-2">
          <Button size="sm" onClick={() => this.setState({ error: null })}>Thử lại</Button>
          <Button size="sm" variant="secondary" onClick={() => window.location.reload()}>Tải lại trang</Button>
        </div>
      </div>
    )
  }
}
